import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Tab,
  Tabs,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import { Google, Email } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`auth-tabpanel-${index}`}
      aria-labelledby={`auth-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export default function LoginModal({
  open,
  onClose,
  defaultUserType = 'investor',
  initialTab = 0,
  lockUserType = false,
  signupContext = null,
  onAuthenticated,
}) {
  const [tabValue, setTabValue] = useState(initialTab);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [userType, setUserType] = useState(defaultUserType);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login, signup, loginWithGoogle } = useAuth();

  useEffect(() => {
    setUserType(defaultUserType);
    if (open) {
      setTabValue(initialTab);
    }
  }, [defaultUserType, initialTab, open]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setLoading(true);
      const result = await login(email, password);
      if (onAuthenticated) {
        onAuthenticated(result);
      }
      onClose();
    } catch (error) {
      setError('Failed to sign in. Please check your credentials.');
    }
    setLoading(false);
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      setError('Password should be at least 6 characters');
      return;
    }
    try {
      setError('');
      setLoading(true);
      const result = await signup(email, password, displayName, userType, signupContext);
      if (onAuthenticated) {
        onAuthenticated(result);
      }
      onClose();
    } catch (error) {
      setError('Failed to create account. Email might already be in use.');
    }
    setLoading(false);
  };

  const handleGoogleLogin = async () => {
    try {
      setError('');
      setLoading(true);
      await loginWithGoogle(userType);
      onClose();
    } catch (error) {
      console.error('Google login error:', error);
      setError(error.message || 'Failed to sign in with Google.');
    }
    setLoading(false);
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setDisplayName('');
    setUserType(defaultUserType);
    setError('');
    setTabValue(0);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h5" align="center" sx={{ fontWeight: 600 }}>
          Welcome to OffMarkets
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="auth tabs">
            <Tab label="Sign In" />
            <Tab label="Sign Up" />
          </Tabs>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <TabPanel value={tabValue} index={0}>
          <Box component="form" onSubmit={handleLogin}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
            />

            {!lockUserType && (
              <FormControl fullWidth margin="normal">
                <InputLabel>I'm a...</InputLabel>
                <Select
                  value={userType}
                  label="I'm a..."
                  onChange={(e) => setUserType(e.target.value)}
                >
                  <MenuItem value="investor">Real Estate Investor</MenuItem>
                  <MenuItem value="wholesaler">Property Wholesaler</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box component="form" onSubmit={handleSignup}>
            <TextField
              fullWidth
              label="Full Name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              helperText="Minimum 6 characters"
            />

            {!lockUserType && (
              <FormControl fullWidth margin="normal">
                <InputLabel>I am a...</InputLabel>
                <Select
                  value={userType}
                  label="I am a..."
                  onChange={(e) => setUserType(e.target.value)}
                  required
                >
                  <MenuItem value="investor">Real Estate Investor</MenuItem>
                  <MenuItem value="wholesaler">Property Wholesaler</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        </TabPanel>

        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              or continue with
            </Typography>
          </Divider>

          <Button
            fullWidth
            variant="outlined"
            startIcon={<Google />}
            onClick={handleGoogleLogin}
            disabled={loading}
            sx={{ mb: 1 }}
          >
            Continue with Google
          </Button>

          <Button
            fullWidth
            variant="outlined"
            startIcon={<Email />}
            onClick={() => setTabValue(tabValue === 0 ? 1 : 0)}
            sx={{ mb: 2 }}
          >
            {tabValue === 0 ? 'Create Account' : 'Sign In Instead'}
          </Button>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={tabValue === 0 ? handleLogin : handleSignup}
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Please wait...' : (tabValue === 0 ? 'Sign In' : 'Sign Up')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
